import { useCallback, useMemo } from "react";
import cx from "ui/cx";
import Input from "ui/forms/Input";
import InputGroup from "ui/forms/InputGroup";
import Select from "ui/forms/Select";
import type { OptionProps } from "ui/forms/Select/Select";
import styles from "./InputGroupDateRange.module.scss";

type PresetKey = "last7" | "last30" | "last90" | "thisMonth" | "thisYear";

interface PresetDefinition {
  label: string;
  getRange: () => { start: string; end: string };
}

const PRESET_DEFINITIONS: Record<PresetKey, PresetDefinition> = {
  last7: {
    label: "Last 7 days",
    getRange: () => ({
      start: offsetDate(-6),
      end: todayStr(),
    }),
  },
  last30: {
    label: "Last 30 days",
    getRange: () => ({
      start: offsetDate(-29),
      end: todayStr(),
    }),
  },
  last90: {
    label: "Last 90 days",
    getRange: () => ({
      start: offsetDate(-89),
      end: todayStr(),
    }),
  },
  thisMonth: {
    label: "This month",
    getRange: () => {
      const now = new Date();
      return {
        start: toLocalDateString(new Date(now.getFullYear(), now.getMonth(), 1)),
        end: todayStr(),
      };
    },
  },
  thisYear: {
    label: "This year",
    getRange: () => {
      const now = new Date();
      return {
        start: toLocalDateString(new Date(now.getFullYear(), 0, 1)),
        end: todayStr(),
      };
    },
  },
};

/**
 * Controls the format of values emitted by onStartChange / onEndChange.
 *
 * - `"date"` (default) — plain `YYYY-MM-DD` string, no time or timezone
 * - `"datetime"` — `YYYY-MM-DDT00:00:00` for start, `YYYY-MM-DDT23:59:59` for end.
 *    No timezone suffix — the consumer decides how to interpret or append one.
 */
export type DateRangeValueFormat = "date" | "datetime";

export interface InputGroupDateRangeProps {
  /** Current start date — accepts YYYY-MM-DD or any string starting with a date */
  startDate: string;
  /** Current end date — accepts YYYY-MM-DD or any string starting with a date */
  endDate: string;
  /** Called when start date changes */
  onStartChange: (value: string) => void;
  /** Called when end date changes */
  onEndChange: (value: string) => void;
  /** Controls the format of emitted values. Defaults to "date". */
  valueFormat?: DateRangeValueFormat;
  /** Label for the start date input */
  startLabel?: string;
  /** Label for the end date input */
  endLabel?: string;
  /** Optional label rendered above the whole group */
  labelText?: string;
  /** Preset quick-select options to show */
  presets?: PresetKey[];
  /** Placeholder text for the preset dropdown */
  presetPlaceholder?: string;
  /** Input size — matches the existing Input component sizes */
  inputSize?: "medium" | "mediumLarge" | "large";
  disabled?: boolean;
  className?: string;
}

/**
 * A date range picker built on native date inputs with optional preset quick-selects.
 *
 * Uses native `<input type="date">` with cross-constraints: the start input's `max`
 * is set to the current end date, and the end input's `min` is set to the current
 * start date, so the browser prevents invalid ranges.
 */
export default function InputGroupDateRange({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  valueFormat = "date",
  startLabel = "From",
  endLabel = "To",
  labelText,
  presets,
  presetPlaceholder = "Quick select",
  inputSize = "mediumLarge",
  disabled = false,
  className,
}: InputGroupDateRangeProps) {
  // Normalize incoming values to YYYY-MM-DD for the native inputs
  const startDateNormalized = toDateOnly(startDate);
  const endDateNormalized = toDateOnly(endDate);

  const presetOptions: OptionProps[] = useMemo(() => {
    if (!presets || presets.length === 0) {
      return [];
    }
    return presets.map((key) => ({
      value: key,
      label: PRESET_DEFINITIONS[key].label,
    }));
  }, [presets]);

  const handlePresetChange = useCallback(
    (value: string | number | boolean | null) => {
      if (!value || typeof value !== "string") {
        return;
      }
      const preset = PRESET_DEFINITIONS[value as PresetKey];
      if (preset) {
        const range = preset.getRange();
        onStartChange(formatOutput(range.start, valueFormat, "start"));
        onEndChange(formatOutput(range.end, valueFormat, "end"));
      }
    },
    [onStartChange, onEndChange, valueFormat],
  );

  const handleStartChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const raw = e.currentTarget.value;
      onStartChange(formatOutput(raw, valueFormat, "start"));
      // If new start is after current end, bump end to match
      if (raw && endDateNormalized && raw > endDateNormalized) {
        onEndChange(formatOutput(raw, valueFormat, "end"));
      }
    },
    [onStartChange, onEndChange, endDateNormalized, valueFormat],
  );

  const handleEndChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const raw = e.currentTarget.value;
      onEndChange(formatOutput(raw, valueFormat, "end"));
      // If new end is before current start, bump start to match
      if (raw && startDateNormalized && raw < startDateNormalized) {
        onStartChange(formatOutput(raw, valueFormat, "start"));
      }
    },
    [onStartChange, onEndChange, startDateNormalized, valueFormat],
  );

  // Determine which preset is currently active (if any).
  // Compare against normalized YYYY-MM-DD so it works regardless of valueFormat.
  const activePreset = useMemo(() => {
    if (!presets) {
      return "";
    }
    for (const key of presets) {
      const range = PRESET_DEFINITIONS[key].getRange();
      if (range.start === startDateNormalized && range.end === endDateNormalized) {
        return key;
      }
    }
    return "";
  }, [presets, startDateNormalized, endDateNormalized]);

  return (
    <InputGroup labelText={labelText}>
      <div className={cx(styles.wrapper, className)}>
        <label className={cx(styles.dateField, styles[inputSize])}>
          <span className={styles.dateLabel}>{startLabel}</span>
          <Input
            type="date"
            value={startDateNormalized}
            max={endDateNormalized || undefined}
            onChange={handleStartChange}
            inputSize={inputSize}
            disabled={disabled}
          />
        </label>
        <label className={cx(styles.dateField, styles[inputSize])}>
          <span className={styles.dateLabel}>{endLabel}</span>
          <Input
            type="date"
            value={endDateNormalized}
            min={startDateNormalized || undefined}
            onChange={handleEndChange}
            inputSize={inputSize}
            disabled={disabled}
          />
        </label>
        {presets && presets.length > 0 && (
          <div className={styles.presetSelect}>
            <Select
              options={presetOptions}
              value={activePreset}
              onChange={handlePresetChange}
              placeholder={presetPlaceholder}
              selectSize={inputSize === "large" ? "large" : "medium"}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </InputGroup>
  );
}

/** Strip any time/timezone suffix to get YYYY-MM-DD for the native input */
function toDateOnly(value: string): string {
  return value.slice(0, 10);
}

function todayStr(): string {
  return toLocalDateString(new Date());
}

function offsetDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalDateString(d);
}

/** Format a Date as YYYY-MM-DD using local time (no timezone shift) */
function toLocalDateString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Format a YYYY-MM-DD string according to the chosen valueFormat */
function formatOutput(dateStr: string, format: DateRangeValueFormat, role: "start" | "end"): string {
  if (!dateStr) {
    return dateStr;
  }
  const dateOnly = toDateOnly(dateStr);
  if (format === "datetime") {
    return role === "start" ? `${dateOnly}T00:00:00` : `${dateOnly}T23:59:59`;
  }
  return dateOnly;
}
